import { NOM_AFFICHE_MAX, type Rapport } from "./contract";
import { COULEUR_UNIQUE, FILAMENTS_AMS_MAX, type LigneFilament } from "./silhouettes-filaments";
import type { Echec, NomLu } from "./silhouettes-jeux";
import { couleursConcernees, lireSaisie, type Saisie } from "./silhouettes-reglages";
import { enumeration, nombreFr } from "./silhouettes-textes";
import type { Colonne, Verdict } from "./silhouettes-verdict";

/**
 * Les textes du mode serre-livres (#337), en fonctions pures : ce que la page dit du verdict de
 * chaque figurine et de ses remedes, du nom, des fichiers eteints, d'un echec de l'assemblage, de
 * chaque ligne de la carte des filaments. Maquettes `docs/maquettes/console-lab/
 * SilhouettesSerreLivres*.dc.html` ; les echecs et les cas de la section 6 de la spec
 * (`docs/specs/2026-10-04-silhouettes-serre-livres-design.md`) sont repris mot pour mot.
 */

/**
 * L'epaisseur de la barre de la base modulaire : ce que « hors tout » ajoute a la hauteur de la
 * silhouette (`base-modulaire.json`, `profondeur_mm` : 4 mm). 77 mm de silhouette font 81 mm hors
 * tout, comme le champ Hauteur et l'aide qu'il porte.
 */
export const BARRE_MM = 4;

/** La mention sous le miroir, au-dessus de l'apercu : l'apercu de l'outil n'est jamais retourne. */
export const MENTION_MIROIR = "en miroir sur l'étagère";

export function legendeDuMiroir(miroir: boolean): string {
  return miroir ? "retourné sur l'étagère" : "tel que dessiné";
}

export function titreDeLaColonne(colonne: Colonne): string {
  return colonne === "gauche" ? "À gauche" : "À droite";
}

export function legendeDeLaColonne(colonne: Colonne): string {
  return colonne === "gauche"
    ? "le mur est à sa droite, les livres derrière"
    : "le mur est à sa gauche, les livres derrière";
}

/** « la figurine de gauche » : de quoi parler d'une figurine dont le nom manque. */
export function figurineDe(colonne: Colonne): string {
  return `la figurine de ${colonne}`;
}

/** Le nom qu'on montre : celui qui est tape et valide, sinon la figurine de sa colonne. */
export function nomOuColonne(nom: NomLu, colonne: Colonne): string {
  return nom.etat === "valide" ? nom.nom : figurineDe(colonne);
}

function majuscule(texte: string): string {
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

/* -------------------------------------------------------------------------------------------
 * Le verdict
 * ----------------------------------------------------------------------------------------- */

/** « 8,1 » et « 6,6 » : un chiffre apres la virgule, comme les planches. */
function debordEtPlace(debord: number, place: number): [string, string] {
  const [court, plein] = [nombreFr(debord, 1), nombreFr(place, 1)];
  // Un debord de 6,64 pour 6,63 de place ne se lit pas « 6,6 pour 6,6 » : la phrase dirait que la
  // figurine deborde de ce qu'on lui laisse. Au centieme, elle ne se contredit pas.
  return court === plein ? [nombreFr(debord, 2), nombreFr(place, 2)] : [court, plein];
}

export type TexteDuVerdict = {
  ton: "bon" | "mauvais" | "neutre";
  /** La phrase en gras. */
  titre: string;
  /** Ce qui la suit. */
  detail: string;
};

/**
 * Ce que dit la carte du verdict. La page ne sait pas ce qui depasse (un bras, une flamme) : elle
 * dit « la figurine ». Les chiffres sont ceux des deux debords deja mesures, aux planches validees
 * le 4 octobre 2026 : un chiffre apres la virgule pour ce qui n'entre pas, deux pour ce qui entre.
 */
export function texteDuVerdict(verdict: Verdict): TexteDuVerdict {
  switch (verdict.etat) {
    case "sans-mesure":
      return {
        ton: "neutre",
        titre: "Pas de verdict.",
        detail: "Le calcul n'a pas mesuré le mur pour cette figurine.",
      };
    case "entre":
      return {
        ton: "bon",
        titre: verdict.justesse
          ? "Entre dans son Bookend, de justesse."
          : "Entre dans son Bookend.",
        detail: `${nombreFr(verdict.debord, 2)} mm côté mur pour ${nombreFr(verdict.place, 2)} de place.`,
      };
    case "retourner": {
      const [debord, place] = debordEtPlace(verdict.debord, verdict.place);
      return {
        ton: "mauvais",
        titre: "N'entre pas dans son Bookend tel quel.",
        detail: `La figurine dépasse de ${debord} mm côté mur, pour ${place} mm de place.`,
      };
    }
    case "aucun-sens": {
      const [debord, place] = debordEtPlace(verdict.debord, verdict.place);
      return {
        ton: "mauvais",
        titre: "N'entre dans aucun sens à cette hauteur.",
        detail: `La figurine dépasse de ${debord} mm côté mur pour ${place} mm de place, et de l'autre côté aussi.`,
      };
    }
  }
}

/** La pastille de la colonne repliee, au telephone : le verdict en deux ou trois mots. */
export function pastilleDuVerdict(
  verdict: Verdict,
): { ton: "bon" | "mauvais"; texte: string } | null {
  switch (verdict.etat) {
    case "sans-mesure":
      return null;
    case "entre":
      return { ton: "bon", texte: verdict.justesse ? "Entre, de justesse" : "Entre" };
    case "retourner":
      return { ton: "mauvais", texte: "N'entre pas tel quel" };
    case "aucun-sens":
      return { ton: "mauvais", texte: "N'entre dans aucun sens" };
  }
}

/** « 81 mm hors tout » pour 77 mm de silhouette : la barre de 4 mm s'ajoute. */
export function horsTout(hauteurSilhouette: number): number {
  return hauteurSilhouette + BARRE_MM;
}

/**
 * Ce que la planche dit sur la ligne de la Hauteur, apres « mm » : « 97 hors tout » pour une
 * silhouette de 93 mm, la barre de 4 mm en plus. Calcule sur la hauteur tapee, il la suit sans
 * attendre le rendu suivant.
 */
export function legendeHorsTout(hauteurSilhouette: number): string {
  return `${nombreFr(horsTout(hauteurSilhouette), 1)} hors tout`;
}

/** Le remede qui retourne la figurine : « Retourner : 6,4 mm, il entre ». */
export function libelleRetourner(debordRetourne: number): string {
  return `Retourner : ${nombreFr(debordRetourne, 1)} mm, il entre`;
}

/** Le remede qui garde le sens : la hauteur de silhouette d'abord, comme le champ Hauteur. */
export function libelleGarderSonSens(hauteur: number): string {
  return `Garder son sens : ${nombreFr(hauteur, 1)} mm (${nombreFr(horsTout(hauteur), 1)} hors tout)`;
}

/** Le remede qui retourne la figurine et change sa hauteur : « Retourner à 81 mm ». */
export function libelleRetournerA(hauteur: number): string {
  return `Retourner à ${nombreFr(hauteur, 1)} mm`;
}

/** Le remede qui garde le sens, tant que le rendu qui le cherche pour ce cote n'est pas revenu. */
export const LIBELLE_SENS_EN_CALCUL = "Garder son sens : hauteur en cours de calcul…";

/** La colonne repliee, au telephone : « 93 mm · 97 hors tout ». */
export function resumeDeLaColonneRepliee(horsToutMm: number): string {
  return `${nombreFr(horsToutMm - BARRE_MM, 1)} mm · ${nombreFr(horsToutMm, 1)} hors tout`;
}

/* -------------------------------------------------------------------------------------------
 * Les reglages plies
 * ----------------------------------------------------------------------------------------- */

/**
 * Le resume, en une ligne, des reglages que la colonne plie sous « Réglages » (Couleurs, Ligne de
 * sol, Hauteur du sol, Traits fins, Traits de couleur), comme les lignes des planches : « couleurs
 * gardées, auto (5 trouvées) · ligne de sol auto (relevée à 5,9 mm) · traits fins : fidèle ». L'attache
 * n'y est pas : elle est toujours la base modulaire, celle du Bookend. Une hauteur de sol refusee n'y
 * est pas non plus : la ligne de resume la signale a part. Les traits de couleur n'y sont que quand
 * des couleurs sont epaissies (#397) : « traits de couleur : blanc épaissi ».
 */
export function resumeDesReglages(saisie: Saisie, rapport: Rapport | null): string {
  const trouvees = rapport?.couleurs?.length;
  const couleurs = saisie.couleursGardees
    ? `couleurs gardées, ${saisie.nombreCouleurs}${
        trouvees === undefined ? "" : ` (${trouvees} trouvée${trouvees > 1 ? "s" : ""})`
      }`
    : "couleurs non gardées";

  let sol = `ligne de sol ${saisie.sol}`;
  if (saisie.sol !== "non") {
    const imposee = saisie.solHauteur.trim();
    if (imposee !== "" && !lireSaisie(saisie).solHauteurInvalide) {
      sol += ` (imposée à ${imposee} mm)`;
    } else if (imposee === "" && rapport?.sol_hauteur_relevee === true) {
      if (rapport.sol_hauteur_mm != null) {
        sol += ` (relevée à ${nombreFr(rapport.sol_hauteur_mm, 1)} mm)`;
      }
    }
  }

  const traits = `traits fins : ${saisie.mode === "fidele" ? "fidèle" : "épaissir"}`;

  // Les couleurs epaissies (#397), dans l'ordre de la ligne qui les coche. Aucune quand les couleurs
  // ne sont pas gardees : la cle ne part pas, et la ligne n'est pas la.
  const epaissies = couleursConcernees(saisie, rapport)
    .filter((couleur) => couleur.epaissie)
    .map((couleur) => couleur.nom);

  const parties = [couleurs, sol, traits];
  if (epaissies.length > 0) {
    const accord = epaissies.length > 1 ? "épaissis" : "épaissi";
    parties.push(`traits de couleur : ${enumeration(epaissies)} ${accord}`);
  }
  return parties.join(" · ");
}

/* -------------------------------------------------------------------------------------------
 * Le nom
 * ----------------------------------------------------------------------------------------- */

const NOM_NOMME_LES_FICHIERS = "il nomme la pièce et le plateau sur MakerWorld";

/** Le texte grise du champ vide : un exemple, celui de la colonne. */
export function exempleDeNom(colonne: Colonne): string {
  return colonne === "gauche" ? "ex. Charmander" : "ex. Squirtle";
}

/**
 * L'aide sous le champ : ce que le nom nomme. Avec les filaments de la figurine, le nom du plateau
 * tel que MakerWorld le montrera (« Charmander - 4 colors »), la regle de `assemblage.py`.
 */
export function aideDuNom(nom: NomLu, filaments: number | null): string {
  if (nom.etat !== "valide") return NOM_NOMME_LES_FICHIERS;
  if (filaments === null) return `${nom.nom} : ${NOM_NOMME_LES_FICHIERS}`;
  return `plateau et pièce sur MakerWorld : « ${nom.nom} - ${filaments} color${filaments > 1 ? "s" : ""} »`;
}

/**
 * Le signalement du champ : un nom qui manque n'en est un que quand la colonne a son image (un nom
 * vide avant la premiere image n'est pas une faute, rien ne peut partir), un nom qui enfreint la
 * regle l'est toujours.
 */
export function erreurDuNom(nom: NomLu, avecImage: boolean): string | null {
  if (nom.etat === "vide") {
    return avecImage ? `Donne un nom : ${NOM_NOMME_LES_FICHIERS}.` : null;
  }
  if (nom.etat === "invalide") {
    return `Lettres, chiffres, espaces et les signes & ' - seulement, ${NOM_AFFICHE_MAX} caractères au plus.`;
  }
  return null;
}

/* -------------------------------------------------------------------------------------------
 * Les fichiers
 * ----------------------------------------------------------------------------------------- */

/** Ce qui est vrai de la page quand les telechargements s'allument, ou pas. */
export type ConditionsDesFichiers = {
  /** Une image par colonne. */
  images: Record<Colonne, boolean>;
  noms: Record<Colonne, NomLu>;
  catalogue: "attente" | "pret" | "erreur";
  /** Les deux rendus repondent aux reglages affiches. */
  rendusPrets: boolean;
};

/**
 * Pourquoi les telechargements sont eteints, ou `null` quand ils ne le sont pas. Dans l'ordre de ce
 * que Nicolas peut faire : deposer les images, donner les noms, puis attendre le catalogue et les
 * rendus.
 */
export function raisonDesFichiersEteints(conditions: ConditionsDesFichiers): string | null {
  const { images, noms, catalogue, rendusPrets } = conditions;
  if (!images.gauche || !images.droite) {
    return "Dépose les deux images, puis donne un nom à chacune.";
  }
  const sansNom = (["gauche", "droite"] as const).filter(
    (colonne) => noms[colonne].etat !== "valide",
  );
  const [premiere, seconde] = sansNom;
  if (premiere !== undefined && seconde !== undefined) {
    return "Il faut un nom valide pour chacune des deux figurines.";
  }
  if (premiere !== undefined) {
    return noms[premiere].etat === "vide"
      ? `Il manque le nom de ${figurineDe(premiere)}.`
      : `Le nom de ${figurineDe(premiere)} n'est pas valide.`;
  }
  if (catalogue === "attente") return "Les teintes Bambu se chargent.";
  if (catalogue === "erreur") {
    return "Les fichiers attendent les teintes Bambu : voir « Filaments du jeu ».";
  }
  if (!rendusPrets) return "Les fichiers arrivent quand les deux rendus sont prêts.";
  return null;
}

export const TITRE_DES_FICHIERS_PRETS =
  "À ouvrir dans Bambu Studio, trancher, puis publier en profil. Rien n'est bloqué quand une figurine n'entre pas : le verdict est au-dessus.";

/**
 * Ce que la carte dit d'un assemblage dont la reponse est revenue apres un changement (un nom, un
 * miroir, une teinte, un rendu) : elle ne parle plus de ce qui est a l'ecran, la page la laisse de
 * cote, et le dit au lieu de ne rien faire.
 */
export const TEXTE_ASSEMBLAGE_CADUC =
  "Ce que tu as changé pendant l'assemblage l'a rendu caduc : relance le téléchargement.";

export const LIBELLE_ASSEMBLAGE_EN_COURS = "Assemblage des deux jeux… jusqu'à 20 s";

export function libelleDuTelechargement(imprimante: "p1s" | "a1mini"): string {
  return imprimante === "p1s"
    ? "Télécharger le jeu complet P1S"
    : "Télécharger le jeu complet A1 mini";
}

export function notePourLImprimante(imprimante: "p1s" | "a1mini"): string {
  return imprimante === "p1s"
    ? "Bambu Lab P1S 0.4, preset 0.20mm Standard P1S Figurine (celui de tes sets), Textured PEI."
    : "Bambu Lab A1 mini 0.4, plateau de 180 mm, preset 0.20mm Standard @BBL A1M.";
}

/** Le refus de l'outil, en phrase : « Squirtle (238 x 184 mm) ne tient pas sur le plateau de 180 mm de l'imprimante A1 mini. » */
export function phraseDuRefus(refus: string): string {
  return /[.!?]$/.test(refus) ? refus : `${refus}.`;
}

export const SUITE_DU_REFUS = "Le jeu P1S se télécharge quand même.";

/* -------------------------------------------------------------------------------------------
 * Un echec de l'assemblage
 * ----------------------------------------------------------------------------------------- */

export type TexteDEchec = {
  /** « rouge » : un refus qu'on ne reessaie pas tel quel ; « gris » : une panne ou une attente. */
  ton: "rouge" | "gris";
  /** Le debut du message, en gras. */
  titre: string;
  /** Ce qui le suit, avec son espace de tete. */
  suite: string;
  /** Vrai : le bouton Reessayer. */
  reessayer: boolean;
  /** Vrai : la page relance d'elle-meme les rendus (le 3MF d'une figurine est a refaire). */
  relance: boolean;
};

/**
 * Ce que la carte Fichiers dit d'un echec (spec, section 6, planche des etats). Le 3MF illisible ne
 * dit pas lequel des deux l'est : le message nomme les deux, et la page relance les deux rendus.
 * Les autres statuts reprennent les messages du rendu, avec « l'assemblage » a la place de « le
 * calcul ».
 */
export function texteDEchec(echec: Echec, noms: Record<Colonne, string>): TexteDEchec {
  switch (echec.genre) {
    case "trop-lourd":
      return {
        ton: "rouge",
        titre: "Ces deux figurines sont trop détaillées pour être assemblées ensemble.",
        suite:
          " Réduis le détail de l'une des deux : moins de couleurs dans ses réglages, ou une image au contour plus simple.",
        reessayer: false,
        relance: false,
      };
    case "illisible":
      return {
        ton: "gris",
        titre: `Le rendu de ${noms.gauche} et de ${noms.droite} est à refaire.`,
        suite: "",
        reessayer: false,
        relance: true,
      };
    case "interrompu":
      return {
        ton: "gris",
        titre: "L'assemblage a pris trop de temps.",
        suite: "",
        reessayer: true,
        relance: false,
      };
    case "refuse":
      return {
        ton: "rouge",
        titre: "L'assemblage a été refusé.",
        suite: ` Le message vient de l'outil : ${echec.message}`,
        reessayer: false,
        relance: false,
      };
    case "invalide":
      return {
        ton: "rouge",
        titre: "Un réglage n'a pas été accepté :",
        suite: " vérifie les noms et les teintes.",
        reessayer: false,
        relance: false,
      };
    case "occupe":
      return {
        ton: "gris",
        titre: "L'assemblage est occupé, réessaie.",
        suite: "",
        reessayer: true,
        relance: false,
      };
    case "indisponible":
      return {
        ton: "gris",
        titre: "Les jeux complets ne sont pas disponibles sur le serveur pour l'instant.",
        suite: "",
        reessayer: true,
        relance: false,
      };
    case "en-panne":
      return {
        ton: "gris",
        titre: "L'assemblage a échoué (erreur interne).",
        suite: " Réessayer ne changera rien : change l'une des deux images, ou ses réglages.",
        reessayer: false,
        relance: false,
      };
    case "ne-repond-pas":
      return {
        ton: "gris",
        titre: "L'assemblage ne répond pas",
        suite: " (rien en 25 s). Tes images et tes réglages sont gardés sur cette page.",
        reessayer: true,
        relance: false,
      };
    case "injoignable":
      return {
        ton: "gris",
        titre: "L'assemblage est injoignable pour l'instant.",
        suite: " Réessaie dans un moment.",
        reessayer: true,
        relance: false,
      };
  }
}

/* -------------------------------------------------------------------------------------------
 * Les filaments
 * ----------------------------------------------------------------------------------------- */

/** La couleur trouvee telle qu'on la lit : `base` est la piece d'une figurine d'une seule couleur. */
export function libelleDeLaCouleur(couleur: string): string {
  return couleur === COULEUR_UNIQUE ? "une seule couleur" : couleur;
}

/** « le noir », « l'orange », « la couleur unique » : de quoi dire avec qui une teinte est partagee. */
function avecArticle(couleur: string): string {
  if (couleur === COULEUR_UNIQUE) return "la couleur unique";
  return /^[aeiouyàâäéèêëîïôöùûüh]/i.test(couleur) ? `l'${couleur}` : `le ${couleur}`;
}

/**
 * La note d'une ligne de la carte des filaments : d'ou vient la teinte, et avec qui elle est
 * partagee. « choisi par toi (propose : Terre cuite) », « retenu de ton dernier choix »,
 * « habituelle », puis « partage avec le noir ». Rien pour une proposition qui n'est pas une
 * habituelle et que personne ne partage. L'outil ne donne pas la teinte la plus proche quand une
 * habituelle passe devant (« a egalite avec Noir ») : la page ne le dit pas.
 */
export function noteDeLaLigne(
  ligne: LigneFilament,
  toutes: readonly LigneFilament[],
  habituelles: readonly string[],
): string {
  const parties: string[] = [];
  const ecart =
    ligne.choisie.cle === ligne.proposee.cle ? "" : ` (proposé : ${ligne.proposee.nom_fr})`;
  if (ligne.origine === "choix") parties.push(`choisi par toi${ecart}`);
  else if (ligne.origine === "memoire") parties.push(`retenu de ton dernier choix${ecart}`);
  else if (habituelles.includes(ligne.choisie.cle)) parties.push("habituelle");
  const autres = toutes.filter(
    (autre) =>
      autre.choisie.cle === ligne.choisie.cle &&
      !(autre.colonne === ligne.colonne && autre.couleur === ligne.couleur),
  );
  if (autres.length > 0) {
    parties.push(`partagé avec ${enumeration(autres.map((autre) => avecArticle(autre.couleur)))}`);
  }
  return parties.join(" ; ");
}

/**
 * L'alerte AMS : un plateau ne tient pas plus de 4 filaments (un AMS, et l'AMS lite de l'A1 mini),
 * et une figurine en a davantage. `nombres` donne les filaments de chaque colonne, `colonnes` celles
 * qui depassent.
 */
export function texteDeLAlerteAms(
  colonnes: readonly Colonne[],
  noms: Record<Colonne, string>,
  nombres: Record<Colonne, number>,
): string {
  const [premiere, seconde] = colonnes;
  if (premiere === undefined) return "";
  const suite = ` : un AMS, comme l'AMS lite de l'A1 mini, en a ${FILAMENTS_AMS_MAX}. Fonds deux couleurs ici ou impose ${FILAMENTS_AMS_MAX} couleurs dans les réglages.`;
  if (seconde === undefined) {
    return `${majuscule(noms[premiere])} a ${nombres[premiere]} filaments${suite}`;
  }
  if (nombres[premiere] === nombres[seconde]) {
    return `${majuscule(noms[premiere])} et ${noms[seconde]} ont chacun ${nombres[premiere]} filaments${suite}`;
  }
  return `${majuscule(noms[premiere])} a ${nombres[premiere]} filaments et ${noms[seconde]} ${nombres[seconde]}${suite}`;
}
